import { Card } from "@/components/ui/card";
import { CheckCircle2, ShieldAlert, Pill, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { NHSContraceptionFormData } from "./NHSContraceptionForm";

interface NHSContraceptionSummaryProps {
  isDarkMode?: boolean;
  data: NHSContraceptionFormData;
}

export function NHSContraceptionSummary({
  isDarkMode = false,
  data,
}: NHSContraceptionSummaryProps) {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "Not specified";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return "Not specified";
    return timeStr;
  };

  const getServiceTypeLabel = (serviceType: string) => {
    const map: Record<string, string> = {
      "oral-contraception": "Oral Contraception",
      "oral-emergency-contraception": "Oral Emergency Contraception",
    };
    return map[serviceType] || serviceType;
  };

  const getOutcomeLabel = (outcome: string) => {
    const map: Record<string, string> = {
      "contraception-supplied": "Contraception supplied",
      "contraception-not-supplied":
        "Contraception not supplied – clinical reason",
      "referred-gp": "Referred to GP",
      "referred-sexual-health": "Referred to sexual health service",
      "non-urgent-signposting": "Non-urgent signposting to another service",
      "urgent-escalation": "Urgent escalation to another service",
      other: "Other",
    };
    return map[outcome] || outcome;
  };

  const getMethodLabel = (method: string) => {
    const map: Record<string, string> = {
      "face-to-face": "Face to Face",
      telephone: "Telephone",
      video: "Video",
    };
    return map[method] || method;
  };

  const getRelationshipLabel = (rel: string) => {
    const map: Record<string, string> = {
      parent: "Parent",
      guardian: "Guardian",
      relative: "Relative",
      carer: "Carer",
      advocate: "Advocate",
      other: "Other",
    };
    return map[rel] || rel;
  };

  const getOecMedicationLabel = (med: string) => {
    const map: Record<string, string> = {
      "levonorgestrel-1500": "Levonorgestrel 1500 mcg (Levonelle / generic)",
      "ulipristal-30": "Ulipristal Acetate 30 mg (ellaOne)",
      "copper-iud-referral": "Copper IUD – referred to another service",
      "not-supplied": "Not supplied",
    };
    return map[med] || med;
  };

  const getReferralDestinationLabel = (dest: string) => {
    const map: Record<string, string> = {
      "gp-practice": "GP Practice",
      "sexual-health": "Sexual Health Service",
      "out-of-hours-gp": "Out of hours GP",
      other: "Other",
    };
    return map[dest] || dest;
  };

  const getEscalationDestinationLabel = (dest: string) => {
    const map: Record<string, string> = {
      "gp-practice": "GP Practice",
      "out-of-hours-gp": "Out of hours GP",
      utc: "UTC (Urgent Treatment Centre)",
      "999": "999",
      ae: "A&E",
      other: "Other",
    };
    return map[dest] || dest;
  };

  const hasReferralDetails =
    data.signpostingRequired === "yes" &&
    (data.signpostingAction === "signposting" ||
      data.signpostingAction === "escalation");

  // ─── Reusable style helpers ────────────────────────────────────────────
  const sectionCard = `rounded-2xl border p-4 space-y-3 transition-colors duration-300 ${
    isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
  }`;
  const sectionHeading = `text-sm font-semibold flex items-center gap-2 ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;
  const rowLabel = `text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`;
  const rowValue = `text-sm font-medium ${
    isDarkMode ? "text-gray-100" : "text-gray-900"
  }`;
  const divider = `border-t ${
    isDarkMode ? "border-gray-700" : "border-gray-100"
  }`;

  const Row = ({
    label,
    value,
  }: {
    label: string;
    value: string | React.ReactNode;
  }) => (
    <div className="flex flex-col gap-0.5">
      <span className={rowLabel}>{label}</span>
      <span className={rowValue}>{value}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* ── Quick actions ───────────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>Quick Actions</p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className={`gap-2 ${
              isDarkMode
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
            onClick={() => window.print()}
          >
            <FileDown className="w-4 h-4" />
            Download Summary
          </Button>
          <div
            className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border ${
              isDarkMode
                ? "border-green-700 bg-green-900/20 text-green-300"
                : "border-green-300 bg-green-50 text-green-700"
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Consultation Completed
          </div>
        </div>
      </div>

      {/* ── Consultation Details ─────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>Consultation Details</p>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Date" value={formatDate(data.consultationDate)} />
          <Row label="Time" value={formatTime(data.consultationTime)} />
          <Row label="Method" value={getMethodLabel(data.consultationMethod)} />
          <Row
            label="Patient Accompanied"
            value={
              data.patientAccompanied === "yes"
                ? `Yes – ${
                    data.accompaniedRelationship === "other"
                      ? data.accompaniedRelationshipOther || "Other"
                      : getRelationshipLabel(data.accompaniedRelationship)
                  }`
                : data.patientAccompanied === "no"
                  ? "No"
                  : "Not specified"
            }
          />
        </div>
      </div>

      {/* ── Safeguarding ────────────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>
          <ShieldAlert className="w-4 h-4 text-amber-500" />
          Safeguarding
        </p>
        <div className="space-y-3">
          <Row
            label="Safeguarding concerns"
            value={
              data.safeguardingConcerns === "yes"
                ? "Yes"
                : data.safeguardingConcerns === "no"
                  ? "No"
                  : "Not specified"
            }
          />
          {data.safeguardingConcerns === "yes" && (
            <>
              <div className={divider} />
              <Row
                label="Followed local safeguarding policy"
                value={
                  data.followedSafeguardingPolicy === "yes"
                    ? "Yes"
                    : data.followedSafeguardingPolicy === "no"
                      ? "No"
                      : "Not specified"
                }
              />
              {data.safeguardingDetails && (
                <>
                  <div className={divider} />
                  <Row
                    label="Details / narrative"
                    value={data.safeguardingDetails}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Service Type ────────────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>Service Type</p>
        <div
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${
            data.serviceType === "oral-emergency-contraception"
              ? isDarkMode
                ? "bg-orange-900/20 border-orange-700 text-orange-300"
                : "bg-orange-50 border-orange-300 text-orange-700"
              : isDarkMode
                ? "bg-blue-900/20 border-blue-700 text-blue-300"
                : "bg-blue-50 border-blue-300 text-blue-700"
          }`}
        >
          <Pill className="w-3.5 h-3.5" />
          {getServiceTypeLabel(data.serviceType) || "Not specified"}
        </div>

        {/* OEC service reason */}
        {data.serviceType === "oral-emergency-contraception" &&
          data.oecServiceReason && (
            <div className="space-y-3 mt-3">
              <div className={divider} />
              <Row
                label="Service reason"
                value={
                  data.oecServiceReason === "initial-supply"
                    ? "Initial Supply"
                    : data.oecServiceReason === "repeat-dose"
                      ? "Repeat Dose"
                      : data.oecServiceReason
                }
              />
            </div>
          )}

        {/* Currently using contraception (OEC) */}
        {data.serviceType === "oral-emergency-contraception" && (
          <div className="space-y-3 mt-3">
            <div className={divider} />
            <Row
              label="Currently using contraception"
              value={
                data.currentlyUsingContraception === "yes"
                  ? `Yes – ${
                      {
                        "combined-pill": "Combined oral contraceptive pill",
                        "progestogen-only-pill": "Progestogen-only pill",
                        condoms: "Condoms (male or female)",
                        implant: "Contraceptive implant",
                        injection: "Contraceptive injection",
                        iud: "Intrauterine device (IUD / coil)",
                        ius: "Intrauterine system (IUS / hormonal coil)",
                        patch: "Contraceptive patch",
                        "vaginal-ring": "Vaginal ring",
                        diaphragm: "Diaphragm or cap",
                        "natural-planning": "Natural family planning",
                        other: "Other",
                      }[data.currentContraceptionMethod] || "Not specified"
                    }`
                  : data.currentlyUsingContraception === "no"
                    ? "No"
                    : "Not specified"
              }
            />
          </div>
        )}
      </div>

      {/* ── Clinical Checks ──────────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>Clinical Checks</p>

        {data.serviceType === "oral-contraception" && (
          <div className="space-y-3">
            <Row
              label="Oral contraception type"
              value={
                data.ocType === "initiation"
                  ? "Initiation"
                  : data.ocType === "ongoing-monitoring"
                    ? "Ongoing Monitoring"
                    : "Not specified"
              }
            />
            {(data.ocType === "initiation" ||
              data.ocType === "ongoing-monitoring") && (
              <>
                <div className={divider} />
                <Row
                  label="Service reason"
                  value={
                    {
                      "first-time":
                        "Initiation of oral contraception – First time pill user",
                      "re-initiation":
                        "Re-initiation of oral contraception – has been on pill before",
                      switching: "Switching oral contraception",
                      "ongoing-gp-srhc":
                        "Ongoing monitoring / supply – GP / SRHC initiated",
                      "ongoing-pharmacist":
                        "Ongoing monitoring / supply – Pharmacist initiated",
                    }[data.ocServiceReason] || "Not specified"
                  }
                />
              </>
            )}
            {(data.ocType === "initiation" ||
              data.ocType === "ongoing-monitoring") && (
              <>
                <div className={divider} />
                <Row
                  label="Currently using contraception"
                  value={
                    data.currentlyUsingContraception === "yes"
                      ? `Yes – ${
                          {
                            "combined-pill": "Combined oral contraceptive pill",
                            "progestogen-only-pill": "Progestogen-only pill",
                            condoms: "Condoms (male or female)",
                            implant: "Contraceptive implant",
                            injection: "Contraceptive injection",
                            iud: "Intrauterine device (IUD / coil)",
                            ius: "Intrauterine system (IUS / hormonal coil)",
                            patch: "Contraceptive patch",
                            "vaginal-ring": "Vaginal ring",
                            diaphragm: "Diaphragm or cap",
                            "natural-planning": "Natural family planning",
                            other: "Other",
                          }[data.currentContraceptionMethod] || "Not specified"
                        }`
                      : data.currentlyUsingContraception === "no"
                        ? "No"
                        : "Not specified"
                  }
                />
              </>
            )}
            <div className={divider} />
            <Row
              label="Gap in oral contraception"
              value={
                data.ocGapInCycle === "yes"
                  ? "Yes"
                  : data.ocGapInCycle === "no"
                    ? "No"
                    : "Not specified"
              }
            />
            <div className={divider} />
            <Row
              label="NCRS checked"
              value={
                data.ncrsChecked === "yes"
                  ? "Yes"
                  : data.ncrsChecked === "no"
                    ? "No"
                    : "Not specified"
              }
            />
            <div className={divider} />
            <Row
              label="EPS checked for contraceptive prescription"
              value={
                data.epsChecked === "yes"
                  ? "Yes"
                  : data.epsChecked === "no"
                    ? "No"
                    : "Not specified"
              }
            />
            <div className={divider} />
            <Row
              label="New symptoms or side effects"
              value={
                data.newSymptomsOrSideEffects === "yes"
                  ? `Yes – ${data.newSymptomsDetails || "No details provided"}`
                  : data.newSymptomsOrSideEffects === "no"
                    ? "No"
                    : "Not specified"
              }
            />
            <div className={divider} />
            <Row
              label="Relevant allergies or adverse reactions"
              value={
                data.relevantAllergies === "yes"
                  ? `Yes – ${data.relevantAllergiesDetails || "No details provided"}`
                  : data.relevantAllergies === "no"
                    ? "No"
                    : "Not specified"
              }
            />
            <div className={divider} />
            <Row
              label="Relevant past medical / surgical / mental health history"
              value={
                data.relevantMedicalHistory === "yes"
                  ? `Yes – ${data.relevantMedicalHistoryDetails || "No details provided"}`
                  : data.relevantMedicalHistory === "no"
                    ? "No"
                    : "Not specified"
              }
            />
          </div>
        )}

        {data.serviceType === "oral-emergency-contraception" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Row label="UPSI date" value={formatDate(data.upsiDate)} />
              <Row label="UPSI time" value={formatTime(data.upsiTime)} />
            </div>
            <div className={divider} />
            <Row
              label="Time since UPSI"
              value={
                data.timeSinceUpsi
                  ? `${data.timeSinceUpsi} hours`
                  : "Not recorded"
              }
            />
            {data.patientWeight && (
              <>
                <div className={divider} />
                <Row
                  label="Patient weight"
                  value={`${data.patientWeight} kg`}
                />
              </>
            )}
            <div className={divider} />
            <Row
              label="EHC supplied"
              value={
                data.oecMedicationSupplied
                  ? getOecMedicationLabel(data.oecMedicationSupplied)
                  : "Not specified"
              }
            />
            <div className={divider} />
            <Row
              label="Contraindications identified"
              value={
                data.oecContraindicationsIdentified === "yes"
                  ? `Yes – ${data.oecContraindicationsDetails || "No details"}`
                  : data.oecContraindicationsIdentified === "no"
                    ? "No"
                    : "Not specified"
              }
            />
          </div>
        )}

        {!data.serviceType && (
          <p
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            No service type selected
          </p>
        )}
      </div>

      {/* ── Oral Contraception Type ──────────────────────────────────── */}
      {data.serviceType === "oral-contraception" && (
        <div className={sectionCard}>
          <p className={sectionHeading}>Oral Contraception Type</p>
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${
              isDarkMode
                ? "bg-blue-900/20 border-blue-700 text-blue-300"
                : "bg-blue-50 border-blue-300 text-blue-700"
            }`}
          >
            <Pill className="w-3.5 h-3.5" />
            {data.ocPillType === "combined"
              ? "Combined Oral Contraceptive"
              : data.ocPillType === "progestogen-only"
                ? "Progestogen Only Pill"
                : "Not specified"}
          </div>
        </div>
      )}

      {/* ── Vital Readings (Combined OC) ─────────────────────────────── */}
      {data.serviceType === "oral-contraception" &&
        data.ocPillType === "combined" &&
        (data.vitalsBpSystolic ||
          data.vitalsWeight ||
          data.vitalsHeight ||
          data.vitalsHeightFeet) && (
          <div className={sectionCard}>
            <p className={sectionHeading}>Vital Readings</p>
            <div className="space-y-3">
              {data.vitalsBpSystolic && data.vitalsBpDiastolic && (
                <Row
                  label="Blood Pressure"
                  value={`${data.vitalsBpSystolic}/${data.vitalsBpDiastolic} mmHg`}
                />
              )}
              {data.vitalsWeight && (
                <>
                  {data.vitalsBpSystolic && <div className={divider} />}
                  <Row
                    label="Weight"
                    value={`${data.vitalsWeight} ${data.vitalsWeightUnit || "kg"}`}
                  />
                </>
              )}
              {(data.vitalsHeight || data.vitalsHeightFeet) && (
                <>
                  {(data.vitalsBpSystolic || data.vitalsWeight) && (
                    <div className={divider} />
                  )}
                  <Row
                    label="Height"
                    value={
                      data.vitalsHeightUnit === "ft/in"
                        ? `${data.vitalsHeightFeet || 0}'${data.vitalsHeightInches || 0}"`
                        : `${data.vitalsHeight} cm`
                    }
                  />
                </>
              )}
              <div className={divider} />
              <p
                className={`text-xs italic ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                These readings have been recorded as vitals in the actions
                panel.
              </p>
            </div>
          </div>
        )}

      {/* ── Supply Details (OC) ────────────────────────────────────── */}
      {data.serviceType === "oral-contraception" && (
        <div className={sectionCard}>
          <p className={sectionHeading}>Supply Details</p>
          <div className="space-y-3">
            <Row
              label="Has oral contraception been supplied?"
              value={
                data.ocSupplied === "yes"
                  ? "Yes"
                  : data.ocSupplied === "no"
                    ? "No"
                    : "Not specified"
              }
            />
            {data.ocSupplied === "yes" &&
              data.suppliedProducts &&
              data.suppliedProducts.length > 0 && (
                <>
                  <div className={divider} />
                  <div className="space-y-2">
                    <span className={rowLabel}>Supplied Products</span>
                    {data.suppliedProducts.map((product, idx) => (
                      <div
                        key={idx}
                        className={`rounded-lg border p-2.5 space-y-1 ${
                          isDarkMode
                            ? "border-gray-600 bg-gray-700/40"
                            : "border-gray-200 bg-gray-50"
                        }`}
                      >
                        <p
                          className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {product.productPrescribed}
                        </p>
                        <p
                          className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {product.packDispensed}
                        </p>
                        <div
                          className={`flex gap-3 text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        >
                          {product.numberOfPacks && (
                            <span>Packs: {product.numberOfPacks}</span>
                          )}
                          {product.doseDuration && (
                            <span>Duration: {product.doseDuration}</span>
                          )}
                          {product.daysSupply && (
                            <span>Days: {product.daysSupply}</span>
                          )}
                        </div>
                        {product.doseDirections && (
                          <p
                            className={`text-xs italic ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {product.doseDirections}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            {data.ocSupplied === "no" && data.noSupplyOutcome && (
              <>
                <div className={divider} />
                <Row
                  label="No supply outcome"
                  value={
                    {
                      "clinically-inappropriate":
                        "Clinically inappropriate & referred/signposted",
                      "declined-by-patient": "Declined by patient",
                      other: "Other",
                    }[data.noSupplyOutcome] || data.noSupplyOutcome
                  }
                />
              </>
            )}
            {data.ocSupplied === "no" && data.noSupplyReason && (
              <>
                <div className={divider} />
                <Row
                  label="No supply reason"
                  value={
                    {
                      "consent-not-given": "Consent not given",
                      "under-16-fraser":
                        "Individuals under 16 – assessed as not competent (Fraser Guidelines)",
                      "over-16-lacking-capacity":
                        "Individuals 16+ – assessed as lacking capacity to consent",
                      "known-suspected-pregnancy":
                        "Known or suspected pregnancy",
                      hypersensitivity:
                        "Known hypersensitivity to active ingredient or constituent",
                      "fewer-21-days-childbirth":
                        "Fewer than 21 days after childbirth",
                      "fewer-5-days-miscarriage":
                        "Fewer than 5 days after miscarriage/abortion/ectopic/GTD",
                      "breastfeeding-6-weeks":
                        "Breastfeeding and less than six weeks postpartum",
                      "interacting-medicines": "Interacting medicines",
                      cardiovascular: "Cardiovascular disease related",
                      neurological: "Neurological condition related",
                      cancer: "Cancer related",
                      "gastro-intestinal":
                        "Gastro-intestinal condition related (incl. malabsorption)",
                      "other-health-conditions":
                        "Related to other health conditions",
                      "dvt-risk": "DVT risk concerns",
                      "above-target-bp-bmi":
                        "Above target blood pressure or BMI",
                      "outside-pgd-age":
                        "Patient outside approved PGD age range",
                      "acute-porphyria": "Acute porphyria",
                      other: "Other",
                    }[data.noSupplyReason] || data.noSupplyReason
                  }
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Outcomes ────────────────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>Outcomes</p>
        <div className="space-y-3">
          <Row
            label="LARC or other effective contraception discussed"
            value={
              data.larcDiscussed === "yes"
                ? "Yes"
                : data.larcDiscussed === "no"
                  ? "No"
                  : "Not specified"
            }
          />
          <div className={divider} />
          <Row
            label="Signposting/escalation required"
            value={
              data.signpostingRequired === "yes"
                ? "Yes"
                : data.signpostingRequired === "no"
                  ? "No"
                  : "Not specified"
            }
          />
          {data.signpostingRequired === "yes" && data.signpostingAction && (
            <>
              <div className={divider} />
              <Row
                label="Action required"
                value={
                  data.signpostingAction === "signposting"
                    ? "Signposting"
                    : data.signpostingAction === "escalation"
                      ? "Escalation"
                      : "Not specified"
                }
              />
              {data.signpostingAction === "signposting" &&
                data.referralDestination && (
                  <>
                    <div className={divider} />
                    <Row
                      label="Signposting destination"
                      value={
                        data.referralDestination === "other"
                          ? data.referralDestinationOther ||
                            getReferralDestinationLabel(
                              data.referralDestination,
                            )
                          : getReferralDestinationLabel(
                              data.referralDestination,
                            ) || "Not specified"
                      }
                    />
                  </>
                )}
              {data.signpostingAction === "escalation" &&
                data.escalationDestination && (
                  <>
                    <div className={divider} />
                    <Row
                      label="Escalation to"
                      value={
                        data.escalationDestination === "other"
                          ? data.escalationDestinationOther ||
                            getEscalationDestinationLabel(
                              data.escalationDestination,
                            )
                          : getEscalationDestinationLabel(
                              data.escalationDestination,
                            ) || "Not specified"
                      }
                    />
                    {data.destinationOdsCode && (
                      <>
                        <div className={divider} />
                        <Row
                          label="Destination ODS code"
                          value={data.destinationOdsCode}
                        />
                      </>
                    )}
                  </>
                )}
            </>
          )}
        </div>
      </div>

      {/* ── Advice and Support ───────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>Advice and Support</p>
        <div className="space-y-3">
          <Row label="Advice given" value={data.adviceGiven ? "Yes" : "No"} />
          {data.adviceGiven && data.adviceProvidedTo && (
            <>
              <div className={divider} />
              <Row
                label="Advice provided to"
                value={
                  data.adviceProvidedTo === "patient"
                    ? "Patient"
                    : data.adviceProvidedTo === "advocate"
                      ? "Patient's advocate"
                      : data.adviceProvidedTo
                }
              />
            </>
          )}
        </div>
      </div>

      {/* ── Notes ───────────────────────────────────────────────────────── */}
      {(data.planAndRequestedActions || data.internalNotes) && (
        <div className={sectionCard}>
          <p className={sectionHeading}>Notes</p>
          <div className="space-y-3">
            {data.planAndRequestedActions && (
              <div className="space-y-1">
                <span
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Plan &amp; requested actions
                </span>
                <p
                  className={`text-sm whitespace-pre-wrap ${
                    isDarkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  {data.planAndRequestedActions}
                </p>
              </div>
            )}
            {data.planAndRequestedActions && data.internalNotes && (
              <div className={divider} />
            )}
            {data.internalNotes && (
              <div className="space-y-1">
                <span
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Internal notes
                </span>
                <p
                  className={`text-sm whitespace-pre-wrap ${
                    isDarkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  {data.internalNotes}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
